//library
import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";

//dto
import ErrorResponseDto from "dto/services/error-response-dto";
import { SelectOption } from "dto/components/app-select-input-dto";
import SolutionCategoriesTableProps, { SolutionCategoriesDataDto, SolutionCategoriesUpdateDto } from "dto/components/pages/solution-categories/app-solution-categories-table-dto";

import getSolutionGroup from "services/get-solution-group";

//components
import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";
import AppSelectInput from "components/app-select-input";

//common
import formatIndexNumber from "common/format-index-number";
import serveRequestErrors from "common/serve-request-errors";

//assets
// import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
// import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
// import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";
import { statusOptions } from "constants/status-options";

const SolutionCategoriesTable: React.FC<SolutionCategoriesTableProps> = ({ data, setDisableAddNew, newRow, setNewRow, onUpdatePartners, onCreatePartner, onHandlePageChange, fetching, paginationTable }) => {
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [isReadyData, setIsReadyData] = useState<boolean>(fetching);
	const [categories, setCategories] = useState<SolutionCategoriesDataDto>(data);
	const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const [updatedData, setUpdatedData] = useState<SolutionCategoriesUpdateDto>();
	const isEmptyData = useMemo(() => categories.length === 0 && !fetching, [categories.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        setEditingRow(id);
        setDisableAddNew(true);
    }, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "title" | "description" | "fullDescription" | "category" | "status" , value: string) => {
		const updatedCategories = updatedData ?? categories.find((category) => category.id === id);

		if (updatedCategories) {
			const updatedData = { ...updatedCategories, [field]: value};

			setUpdatedData(updatedData);

			setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" }}));
		}
    }, [categories, updatedData]);

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["title", "description", "fullDescription", "category", "status"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (updatedData) {
			const rowErrors = validateRow(updatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return; 
			}

			const newCategory = categories.map((category) => (category.id === id ? { ...category, ...updatedData } : category));

			setCategories(newCategory);
			onUpdatePartners(updatedData);
			setIsReadyData(false);
			setEditingRow(null);
			setUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} })); 
		}
	}, [categories, updatedData, onUpdatePartners, setDisableAddNew]);

	//prettier-ignore
	// const onHandleMove = useCallback((id: string, move: number) => {
	// 	const index = categories.findIndex((category) => category.id === id);
	// 	const updatedPartner = categories[index];

	// 	if ((index > 0  && move === -1) || (index < categories.length - 1 && move === 1)) {
	// 		const newPartners = [...categories];
	// 		const [movedCareer] = newPartners.splice(index, 1);
	// 		const movedDataSortNumber = categories[index + move].sortNumber;
	// 		const updatedData = { ...updatedPartner, sortNumber: movedDataSortNumber };

	// 		if (updatedData) setUpdatedData(updatedData);

	// 		newPartners.splice(index + move, 0, movedCareer);

	// 	 setCategories(newPartners);
	// 	}
	// }, [categories]);

	//prettier-ignore
	// const onHandleDelete = useCallback((id: string) => {
	// 	const updatedPartner = categories.find((category) => category.id === id);

	// 	if (updatedPartner) {
	//         const updatedData = { ...updatedPartner, status: "inactive" };

	// 		const changePage = paginationTable.numberOfElements < 2 && paginationTable.currentPage > 0 ? paginationTable.currentPage - 1 : undefined;

	// 		onUpdatePartners(updatedData, changePage);
	// 	}

	// 	setEditingRow(null);
	// 	setUpdatedData(null);
	// 	setDisableAddNew(false);
	// }, [categories, paginationTable, onUpdatePartners, setDisableAddNew]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setEditingRow(null);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const newCategory = {
			title: newRow?.title || "",
			description: newRow?.description || "",
			fullDescription: newRow?.fullDescription || "",
			category: newRow?.category || "",
			status: "active",
		};

		onCreatePartner(newCategory);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	const getCategoryTitle = (category: string) => {
		return categoryOptions.find((opt) => opt.value === category)?.label || category;
	};

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		return options.find((option) => option.value === value)?.label || value;
	};

	useEffect(() => {
		if (!fetching) setIsReadyData(true);
	}, [fetching]);

	useEffect(() => {
		if (!editingRow && isReadyData) setCategories(data);
	}, [categories, editingRow, isReadyData, data]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const response = await getSolutionGroup();

				setCategoryOptions(response.data.content);
			} catch (error) {
				const err = error as Error | ErrorResponseDto;

				serveRequestErrors(err);
			}
		};
		fetchCategories();
	}, []);

	return (
		<div className="app-solution-categories-table">
			<div className="categories-table">
				<div className="categories-table__wrapper">
					<table className="categories-table__container">
						<thead className="categories-table__header">
							<tr>
								<th className="categories-table__th">No.</th>
								<th className="categories-table__th">Title</th>
								<th className="categories-table__th">Description</th>
								<th className="categories-table__th">Full Description</th>
								<th className="categories-table__th">Group</th>
								<th className="categories-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="categories-table__content">
							{categories.map((item, index) => {
								const isEditing = editingRow === item.id;
								const isExpanded = expandedRows.includes(item.id);
								const rowErrors = errors[item.id] || {};

								return (
									<tr className="categories-table__tr" key={item.id}>
										<td className="categories-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="categories-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="title" error={rowErrors.title} touched={!!updatedData} value={updatedData?.title ?? item.title} maxCharLength={90} maxRows={20} onChange={(e) => onHandleInputChange(item.id, "title", e.target.value)} />
											) : isExpanded ? (
												item.title
											) : (
												truncateText(item.title, 30)
											)}
										</td>

										<td className="categories-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="description" error={rowErrors.description} touched={!!updatedData} value={updatedData?.description ?? item.description} maxCharLength={150} maxRows={30} onChange={(e) => onHandleInputChange(item.id, "description", e.target.value)} />
											) : isExpanded ? (
												item.description
											) : (
												truncateText(item.description, 55)
											)}
										</td>

										<td className="categories-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="fullDescription" error={rowErrors.fullDescription} touched={!!updatedData} value={updatedData?.fullDescription ?? item.fullDescription} maxCharLength={600} maxRows={50} onChange={(e) => onHandleInputChange(item.id, "fullDescription", e.target.value)} />
											) : isExpanded ? (
												item.fullDescription
											) : (
												truncateText(item.fullDescription, 90)
											)}
										</td>

										<td className="categories-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="category" error={rowErrors.category} touched={!!updatedData} placeholder={item.category} value={updatedData?.category ?? item.category } onChange={(e) => onHandleInputChange(item.id, "category", e.target.value)} loadOptions={() => getSolutionGroup()} searchable={false} />
											) : (
												getCategoryTitle(item.category)
											)}
										</td>

										<td className="categories-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="status" error={rowErrors.status} touched={!!updatedData} placeholder={item.status} value={updatedData?.status ?? item.status} onChange={(e) => onHandleInputChange(item.id, "status", e.target.value)} options={statusOptions} searchable={false} />
											) : (
												<span className={item.status === "active" ? "categories-table__td--active" : "categories-table__td--inactive"}>{getLabel(statusOptions, item.status)}</span>
											)}
										</td>

										<td className="categories-table__td">
											{isEditing ? (
												<div className="categories-table__edit">
													<button className="categories-table__cancel" onClick={onHandleCancel}>
														Cancel
													</button>

													{/* <button className="categories-table__icon-button--bordered" onClick={() => onHandleDelete(item.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button> */}

													{/* <button className="categories-table__icon-button--bordered" onClick={() => onHandleMove(item.id, -1)}>
														<img src={arrowUp} alt="up" />
													</button>

													<button className="categories-table__icon-button--bordered" onClick={() => onHandleMove(item.id, 1)}>
														<img src={arrowDown} alt="down" />
													</button> */}

													<button className="categories-table__icon-button--save" onClick={() => onHandleSave(item.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="categories-table__actions">
													<button className="categories-table__icon-button" onClick={() => toggleRow(item.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="categories-table__icon-button" onClick={() => onHandleEdit(item.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="categories-table__tr" ref={newRowRef}>
									<td className="categories-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="categories-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-title" error={errors["new"]?.title} touched={!!newRow} fieldRequired value={newRow?.title || ""} maxCharLength={90} maxRows={20} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("title", e.target.value)} />
									</td>

									<td className="categories-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-description" error={errors["new"]?.description} touched={!!newRow} fieldRequired value={newRow?.description || ""} maxCharLength={150} maxRows={30} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("description", e.target.value)} />
									</td>

									<td className="categories-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-fullDescription" error={errors["new"]?.fullDescription} touched={!!newRow} fieldRequired value={newRow?.fullDescription || ""} maxCharLength={250} maxRows={50} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("fullDescription", e.target.value)} />
									</td>

									<td className="categories-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="new-category" error={errors["new"]?.category} touched={!!newRow} placeholder="Please Select" value={newRow?.category || ""} onChange={(e) => onHandleNewInputChange("category", e.target.value)} loadOptions={() => getSolutionGroup()} searchable={false} />
									</td>

									<td className="categories-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="status" error={errors["new"]?.status} touched={!!newRow} placeholder="Please Select" value={newRow?.status || ""} onChange={(e) => onHandleNewInputChange("status", e.target.value)} options={statusOptions} searchable={false} />
									</td>

									<td className="categories-table__td">
										<div className="categories-table__actions">
											<button className="categories-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="categories-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="categories-table__tr">
									<td className="categories-table__message" colSpan={6}>
										Loading...
									</td>
								</tr>
							)}
							{isEmptyData && (
								<tr className="categories-table__tr">
									<td className="categories-table__message" colSpan={6}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default SolutionCategoriesTable;
